import type {
  AttachmentSimpleResponse,
  InspectionDeviceHistoryEntryResponse,
  InspectionOfflineBundleResponse,
  InspectionScopeDeviceResponse,
} from 'src/api/ProactApiClient';
import {
  cacheFilesForSession,
  getSessionCachedRefKeys,
} from 'src/offline/cachedFiles';
import {readSnapshot} from 'src/offline/offlineQuery';
import type {HydrationPlanEntry} from 'src/offline/offlineSessionManager';
import {
  getActiveSession,
  markSessionReady,
  startOfflineSession,
} from 'src/offline/offlineSessionManager';
import {ensureCapacity, requestPersistence} from 'src/offline/quota';
import type {OfflineSession} from 'src/offline/types';
import {store} from 'src/redux/store';
import {getUrlWithEncodingAndAuth} from 'src/shared/utils/urlHelpers';
import {storeBaseSnapshots} from './localState';
import {inspectionContextKey, inspectionSnapshotKeys} from './snapshotKeys';

const PHOTO_CONCURRENCY = 4;
const PHOTO_RETRIES = 2;
// AttachmentSimpleResponse carries no byte size, so estimate for the pre-fetch
// quota guard; actual blob sizes are still stored per file.
const ESTIMATED_PHOTO_BYTES = 2_000_000;
const CAPTURE_HEADROOM_BYTES = 200_000_000;

export interface CachedPhotoTarget {
  deviceId: number;
  attachment: AttachmentSimpleResponse;
}

export interface DownloadProgress {
  phase: 'bundle' | 'photos' | 'ready';
  photosDone: number;
  photosTotal: number;
  photosFailed: number;
  persistenceDenied: boolean;
}

export type DownloadProgressCallback = (progress: DownloadProgress) => void;

/** Supplied by the caller — this tier may not import the API client. */
export type FetchInspectionBundle = (
  inspectionId: number
) => Promise<InspectionOfflineBundleResponse>;

export const collectHistoryAttachments = (
  bundle: InspectionOfflineBundleResponse
): CachedPhotoTarget[] =>
  bundle.deviceHistory.flatMap(history =>
    history.entries.flatMap(entry =>
      entry.attachments.map(attachment => ({
        deviceId: history.deviceId,
        attachment,
      }))
    )
  );

/** Targets not yet in the files table (resume by diffing cached refKeys). */
export const diffUncachedTargets = (
  targets: CachedPhotoTarget[],
  cachedRefKeys: Set<string>
): CachedPhotoTarget[] =>
  targets.filter(target => !cachedRefKeys.has(target.attachment.key));

export const buildInspectionHydrationPlan = (
  inspectionId: number,
  bundle: InspectionOfflineBundleResponse
): HydrationPlanEntry[] => {
  const plan: HydrationPlanEntry[] = [
    {
      key: inspectionSnapshotKeys.meta(inspectionId),
      run: async () => bundle.inspection,
    },
    {
      key: inspectionSnapshotKeys.devices(inspectionId),
      run: async () => bundle.devices,
    },
    {
      key: inspectionSnapshotKeys.definitions(inspectionId),
      run: async () => bundle.fieldSets,
    },
  ];
  bundle.deviceHistory.forEach(history => {
    plan.push({
      key: inspectionSnapshotKeys.history(inspectionId, history.deviceId),
      run: async () => history.entries,
    });
  });
  return plan;
};

const collectTargetsFromSnapshots = async (
  session: OfflineSession,
  inspectionId: number
): Promise<CachedPhotoTarget[]> => {
  const devicesSnap = await readSnapshot(
    session.id,
    inspectionSnapshotKeys.devices(inspectionId)
  );
  const devices =
    (devicesSnap?.data as InspectionScopeDeviceResponse[] | undefined) ?? [];

  const targets: CachedPhotoTarget[] = [];
  for (const device of devices) {
    const historySnap = await readSnapshot(
      session.id,
      inspectionSnapshotKeys.history(inspectionId, device.deviceId)
    );
    const history = historySnap?.data as
      | InspectionDeviceHistoryEntryResponse[]
      | undefined;
    history?.forEach(entry =>
      entry.attachments.forEach(attachment =>
        targets.push({deviceId: device.deviceId, attachment})
      )
    );
  }
  return targets;
};

// Downloads the version-pinned bundle then its history photos, marking the
// session ready only once every step completes; safe to re-run to resume.
export const downloadInspectionForOffline = async (
  inspectionId: number,
  fetchBundle: FetchInspectionBundle,
  onProgress: DownloadProgressCallback = () => {}
): Promise<void> => {
  const contextKey = inspectionContextKey(inspectionId);
  const persistenceDenied = !(await requestPersistence());
  const report = (
    phase: DownloadProgress['phase'],
    photosDone: number,
    photosTotal: number,
    photosFailed = 0
  ): void =>
    onProgress({
      phase,
      photosDone,
      photosTotal,
      photosFailed,
      persistenceDenied,
    });

  let session = await getActiveSession(contextKey);
  if (session?.ready) {
    report('ready', 0, 0);
    return;
  }

  if (!session) {
    report('bundle', 0, 0);
    const bundle = await fetchBundle(inspectionId);
    session = await startOfflineSession({
      contextKey,
      hydrationPlan: buildInspectionHydrationPlan(inspectionId, bundle),
    });
  }

  const targets = await collectTargetsFromSnapshots(session, inspectionId);
  const cachedRefKeys = await getSessionCachedRefKeys(session.id);
  const toFetch = diffUncachedTargets(targets, cachedRefKeys);
  let done = targets.length - toFetch.length;
  let failed = 0;
  report('photos', done, targets.length);

  if (toFetch.length > 0) {
    await ensureCapacity(
      toFetch.length * ESTIMATED_PHOTO_BYTES,
      CAPTURE_HEADROOM_BYTES
    );
    const token = store.getState().auth.accessToken;
    const result = await cacheFilesForSession({
      sessionId: session.id,
      targets: toFetch.map(({attachment}) => ({
        refKey: attachment.key,
        name: attachment.name,
        url: getUrlWithEncodingAndAuth(attachment.contentUrl, token),
      })),
      concurrency: PHOTO_CONCURRENCY,
      retries: PHOTO_RETRIES,
      onProgress: (cached, failures) =>
        report('photos', done + cached, targets.length, failures),
    });
    done += result.done;
    failed = result.failed;
  }

  await storeBaseSnapshots(session.id, inspectionId);
  await markSessionReady(session.id);
  report('ready', done, targets.length, failed);
};
